import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import { motion } from 'framer-motion';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'icon';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
  /**
   * Accessible label for icon-only buttons (REQUIRED for icon variant)
   * Improves accessibility for screen readers
   */
  ariaLabel?: string;
}

/**
 * Button component following Notion design patterns
 * - Icons are 16px when paired with text
 * - Border radius: 8px
 * - Heights: 32px (sm), 40px (md), 48px (lg)
 * - Font: 14px, weight 600
 * - Transitions: 200ms
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ariaLabel,
  ...props
}) => {
  // Base classes - applies to all buttons
  const baseClasses =
    'inline-flex items-center justify-center font-semibold ' +
    'transition-all duration-200 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

  // Variant styles
  const variantClasses = {
    primary:
      'bg-orange-400 text-white border-none ' +
      'hover:bg-orange-300 hover:shadow-md ' +
      'active:bg-orange-500 active:scale-[0.98]',

    secondary:
      'bg-transparent text-orange-400 border-2 border-orange-400 ' +
      'hover:bg-orange-50 hover:border-orange-300 ' +
      'active:bg-orange-100',

    tertiary:
      'bg-transparent text-black-light border-none ' +
      'hover:bg-cream-100 hover:text-black ' +
      'active:bg-cream-200',

    icon:
      'bg-transparent text-black-light border-none ' +
      'hover:bg-cream-100 ' +
      'active:bg-cream-200 ' +
      'p-0',
  };

  // Size classes - matching Notion specs
  const sizeClasses = {
    sm: variant === 'icon'
      ? 'w-8 h-8 rounded'
      : 'h-8 px-3 text-xs gap-2 rounded',

    md: variant === 'icon'
      ? 'w-10 h-10 rounded-md'
      : 'h-10 px-5 text-base gap-2 rounded-md',

    lg: variant === 'icon'
      ? 'w-12 h-12 rounded-md'
      : 'h-12 px-6 text-base-lg gap-2 rounded-md',
  };

  const combinedClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // For icon-only buttons, require aria-label for accessibility
  const accessibilityProps = variant === 'icon' && !children
    ? { 'aria-label': ariaLabel || 'Button', role: 'button' }
    : {};

  const MotionButton = motion.button as any;

  return (
    <MotionButton
      className={combinedClasses}
      disabled={disabled || isLoading}
      whileTap={{ scale: disabled || isLoading ? 1 : 0.95 }}
      whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
      transition={{ duration: 0.15 }}
      {...accessibilityProps}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {children && <span>Loading...</span>}
        </>
      ) : (
        <>
          {leftIcon && <span className="shrink-0 w-4 h-4">{leftIcon}</span>}
          {children && <span>{children}</span>}
          {rightIcon && <span className="shrink-0 w-4 h-4">{rightIcon}</span>}
        </>
      )}
    </MotionButton>
  );
};
